import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import Slider from 'react-slick';
import { useQuery } from '@apollo/client';
import { Container, Icon } from '@pella/sash-framework';
import { CTA } from '../../../';
import { contentstackClient } from '../../../../utils/contentstack-client';
import { GET_ARTICLES_BY_TAXONOMY, GET_INSPIRATION_BY_TAXONOMY } from '../../../../enums/contentstack-graphql';
import { setLocalStorage, getLocalStorage, removeLocalStorage, setBackgroundColor } from '../../../utilities/functions';
import { isValidArray } from '../../../utilities/validations';

import BaseImageCard from '../../../common/image-card';

import styles from './index.module.scss';

const NextArrow = (props) => {
	const { className, style, onClick } = props;
	return (
		<button
			className={`inspire-next absolute -right-3 top-1/2 z-5 flex h-28 w-7 -translate-y-1/2 items-center justify-center border border-black bg-white text-black shadow-md md:-right-5 md:h-10 md:w-10 ${className}`}
			style={{ ...style }}
			onClick={onClick}
			aria-label='Next'
		>
			<Icon
				name='arrow_right'
				className='block'
			/>
		</button>
	);
};

NextArrow.propTypes = {
	className: PropTypes.string,
	style: PropTypes.object,
	onClick: PropTypes.func,
};

const PrevArrow = (props) => {
	const { className, style, onClick } = props;
	return (
		<button
			className={`inspire-previous absolute -left-3 top-1/2 z-5 flex h-28 w-7 -translate-y-1/2 items-center justify-center border border-black bg-white text-black shadow-md md:-left-5 md:h-10 md:w-10 ${className}`}
			style={{ ...style }}
			onClick={onClick}
			aria-label='Previous'
		>
			<Icon
				name='arrow_left'
				className='block'
			/>
		</button>
	);
};

PrevArrow.propTypes = {
	className: PropTypes.string,
	style: PropTypes.object,
	onClick: PropTypes.func,
};

const Inspiration = ({ data, dynamic, className, checkDynamic }) => {
	const sliderRef = useRef(null);
	const filters = isValidArray(dynamic?.filter_tags) ? dynamic.filter_tags.map((filter) => ({ taxonomies: { filter_by: { term_in: filter?.split(', ') } } })) : [];
	const { data: articleData } = useQuery(GET_ARTICLES_BY_TAXONOMY, { client: contentstackClient, variables: { filters, limit: 7 }, skip: !dynamic?.pro || !dynamic?.filter_tags, onCompleted: (data) => onLoadComplete(data?.all_articles?.items) });
	const { data: imageData } = useQuery(GET_INSPIRATION_BY_TAXONOMY, { client: contentstackClient, variables: { filters, limit: 7 }, skip: dynamic?.pro || !dynamic?.filter_tags, onCompleted: (data) => onLoadComplete(data?.all_image_detail?.items) });

	useEffect(() => {
		const scrollPoint = getLocalStorage('scrollPoint');
		if (scrollPoint && scrollPoint.closeURL && scrollPoint.position) {
			if (typeof window !== 'undefined' && typeof scrollTo !== 'undefined') {
				(window.location.pathname == scrollPoint.closeURL &&
					setTimeout(() => {
						scrollTo(scrollPoint.position.offSetX, scrollPoint.position.offSetY);
					}, 200)) ||
					'';
				removeLocalStorage('scrollPoint');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, []);

	const handleClick = () => {
		let position = {
			offSetX: (typeof window !== 'undefined' && window.scrollX) || 0,
			offSetY: (typeof window !== 'undefined' && window.scrollY) || 0,
		};

		let flattenURLs;
		if (isValidArray(imageData)) {
			flattenURLs = imageData.map((item) => item.url);
		} else if (isValidArray(dynamic?.manual_entries)) {
			flattenURLs = dynamic.manual_entries.map((item) => item.url);
		}

		const imgDetailData = {
			position,
			slideData: flattenURLs,
			currentUrl: window.location.pathname,
		};

		setLocalStorage('imgDetailData', imgDetailData);
	};

	const renderSlideCard = (item, index) => {
		const graphImage = item.imageConnection?.edges?.[0]?.node;
		const image = isValidArray(item.image) ? item.image : [{ image: graphImage?.imageConnection?.edges?.[0]?.node, shop_the_look: graphImage?.shop_the_look }];

		return (
			<div
				key={index}
				className={styles.inspiration_Slide}
				id={`inspirationSlide-${index}`}
			>
				<Link
					to={item.url || item?.ctas?.[0]?.link?.href}
					onClick={item.url ? () => handleClick() : null}
					className={styles.inspiration_SlideButton}
				>
					<BaseImageCard
						data={{
							...item,
							image: image,
						}}
						disableLink
					/>
				</Link>
			</div>
		);
	};

	const renderProSlideCard = (item, index) => (
		<div
			key={index}
			className={styles.inspiration_Slide}
			id={`inspirationSlide-${index}`}
		>
			<Link
				to={item.url}
				className={styles.inspiration_SlideButton}
			>
				<BaseImageCard
					data={{
						image: item.modular_blocks?.find((block) => block.all_images != null)?.all_images?.image?.[0],
						heading: item.seo?.title,
					}}
					disableLink
				/>
			</Link>
		</div>
	);

	const onLoadComplete = (array) => {
		if (!isValidArray(array) && typeof checkDynamic === 'function') {
			checkDynamic('inspiration', false);
		}
	};

	if (!isValidArray(imageData?.all_image_detail?.items) && !isValidArray(articleData?.all_articles?.items) && !isValidArray(dynamic?.manual_entries)) return null;

	return (
		<div className={`${styles.inspiration} ${data.background_color ? styles[setBackgroundColor(data.background_color)] : ''} ${className || ''}`}>
			{data.heading && (
				<Container>
					<h2 className='mb-5 md:mb-6 xl:mb-10'>{data.heading}</h2>
				</Container>
			)}

			<div className={styles.inspiration_SliderWrapper}>
				<Container>
					<Slider
						slidesToShow={3}
						slidesToScroll={1}
						arrows={true}
						dots={true}
						prevArrow={<PrevArrow />}
						nextArrow={<NextArrow />}
						className={styles.inspiration_Slider}
						infinite={false}
						draggable={false}
						swipeToSlide={true}
						ref={(slider) => (sliderRef.current = slider)}
						// beforeChange={(curr, next) => setSlideIndex(next)}
						responsive={[
							{
								breakpoint: 1279,
								settings: { slidesToShow: 3 },
							},
							{
								breakpoint: 767,
								settings: { slidesToShow: 2 },
							},
							{
								breakpoint: 600,
								settings: { slidesToShow: 1 },
							},
						]}
					>
						{isValidArray(imageData?.all_image_detail?.items) && imageData.all_image_detail.items.map((item, index) => renderSlideCard(item, index))}
						{isValidArray(articleData?.all_articles?.items) && articleData.all_articles.items.map((item, index) => renderProSlideCard(item, index))}
						{isValidArray(dynamic?.manual_entries) && dynamic.manual_entries.map((item, index) => renderSlideCard(item, index))}
					</Slider>
				</Container>
			</div>

			{isValidArray(dynamic.ctas) && (
				<div className={styles.inspiration_CTAWrapper}>
					<Container>
						<CTA data={dynamic.ctas[0]} />
					</Container>
				</div>
			)}
		</div>
	);
};

Inspiration.propTypes = {
	data: PropTypes.object,
	dynamic: PropTypes.object,
	className: PropTypes.string,
	checkDynamic: PropTypes.func,
};

export default Inspiration;
